import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Edit,
  Eye,
  Filter,
  FlaskConical,
  Loader2,
  Play,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { useToast } from '../components/Toast';
import { ConfirmModal } from '../components/ConfirmModal';
import {
  deleteTestCase,
  getTestCases,
  importTestCases,
  PRIORITY_BADGE,
  STATUS_BADGE,
  TEST_ACTORS,
  TEST_MODULES,
  TestCase,
  TestCaseListQuery,
  TestCasePriority,
  TestCaseStatus,
} from '../services/testCasesApi';

type SortField = 'moduleId' | 'name' | 'priority' | 'status' | 'primaryActor' | 'createdAt';

const PRIORITY_LABEL: Record<TestCasePriority, string> = {
  CRITICAL: 'Critique',
  HIGH: 'Haute',
  MEDIUM: 'Moyenne',
  LOW: 'Basse',
};

const STATUS_LABEL: Record<TestCaseStatus, string> = {
  DRAFT: 'Brouillon',
  ACTIVE: 'Actif',
  DEPRECATED: 'Déprécié',
};

function sortValue(tc: TestCase, field: SortField): string | number {
  switch (field) {
    case 'moduleId': return tc.moduleId;
    case 'name': return tc.name.toLowerCase();
    case 'priority': return ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].indexOf(tc.priority);
    case 'status': return tc.status;
    case 'primaryActor': return tc.primaryActor;
    case 'createdAt': return new Date(tc.createdAt).getTime();
    default: return '';
  }
}

export function TestCasesListPage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const importRef = useRef<HTMLInputElement>(null);

  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<TestCaseStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TestCasePriority | ''>('');
  const [actorFilter, setActorFilter] = useState('');

  const [sortKey, setSortKey] = useState<SortField>('moduleId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({
    open: false, id: '', name: '',
  });

  const load = async (query?: TestCaseListQuery) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTestCases(query);
      setTestCases(data);
    } catch (err: any) {
      const msg = err.message || 'Erreur lors du chargement';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = testCases.filter((tc) => {
      if (moduleFilter && tc.moduleId !== moduleFilter) return false;
      if (statusFilter && tc.status !== statusFilter) return false;
      if (priorityFilter && tc.priority !== priorityFilter) return false;
      if (actorFilter && tc.primaryActor !== actorFilter) return false;
      if (term) {
        return [tc.name, tc.moduleId, tc.description ?? '', ...tc.tags].join(' ').toLowerCase().includes(term);
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [testCases, search, moduleFilter, statusFilter, priorityFilter, actorFilter, sortKey, sortDir]);

  // Stats counters
  const priorityCounts = useMemo(() => {
    const counts: Partial<Record<TestCasePriority, number>> = {};
    for (const tc of testCases) counts[tc.priority] = (counts[tc.priority] ?? 0) + 1;
    return counts;
  }, [testCases]);

  const statusCounts = useMemo(() => {
    const counts: Partial<Record<TestCaseStatus, number>> = {};
    for (const tc of testCases) counts[tc.status] = (counts[tc.status] ?? 0) + 1;
    return counts;
  }, [testCases]);

  const handleSort = (key: SortField) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortKey !== field) return <ChevronDown className="w-3 h-3 text-gray-400 opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-amber-600" />
      : <ChevronDown className="w-3 h-3 text-amber-600" />;
  };

  const handleDelete = async () => {
    try {
      await deleteTestCase(deleteConfirm.id);
      showToast('Cas de test supprimé', 'success');
      setDeleteConfirm({ open: false, id: '', name: '' });
      setSelected((prev) => { const n = new Set(prev); n.delete(deleteConfirm.id); return n; });
      void load();
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de la suppression', 'error');
    }
  };

  const handleBulkDelete = async () => {
    try {
      setBulkDeleting(true);
      const ids = [...selected];
      await Promise.all(ids.map((id) => deleteTestCase(id)));
      showToast(`${ids.length} cas de test supprimé${ids.length > 1 ? 's' : ''}`, 'success');
      setSelected(new Set());
      setBulkDeleteConfirm(false);
      void load();
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de la suppression', 'error');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const toggleSelectAll = () => {
    if (selected.size === filtered.length && filtered.every((tc) => selected.has(tc.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((tc) => tc.id)));
    }
  };

  const allSelected = filtered.length > 0 && filtered.every((tc) => selected.has(tc.id));
  const someSelected = selected.size > 0 && !allSelected;

  const handleExport = () => {
    const payload = {
      $schema: 'digital-tattoo-testcase/v1',
      exportedAt: new Date().toISOString(),
      testCases: testCases.filter((tc) => tc.status !== 'DEPRECATED'),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-cases-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setImporting(true);
      const text = await file.text();
      const payload = JSON.parse(text);
      const result = await importTestCases(payload);
      showToast(
        `Import terminé : ${result.imported} importés, ${result.updated} mis à jour, ${result.skipped} ignorés${result.errors.length ? `, ${result.errors.length} erreurs` : ''}`,
        result.errors.length ? 'error' : 'success',
        8000,
      );
      void load();
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de l\'import', 'error');
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
    }
  };

  const moduleName = (id: string) => TEST_MODULES.find((m) => m.id === id)?.label ?? id;
  const actorName = (id: string) => TEST_ACTORS.find((a) => a.id === id)?.label ?? id;
  const hasActiveFilter = !!(search || moduleFilter || statusFilter || priorityFilter || actorFilter);

  return (
    <div className="space-y-4">
      <ToastComponent />
      <input ref={importRef} type="file" accept=".json" className="hidden" onChange={handleImportFile} />

      {/* ── Header bar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100 flex-shrink-0">
            <FlaskConical className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Cas de test</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {testCases.length} cas de test{filtered.length !== testCases.length ? ` · ${filtered.length} affiché${filtered.length > 1 ? 's' : ''}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <button
            onClick={() => importRef.current?.click()}
            disabled={importing}
            className="btn-secondary inline-flex items-center gap-1.5 text-sm"
          >
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Importer
          </button>
          <button onClick={handleExport} className="btn-secondary inline-flex items-center gap-1.5 text-sm">
            <Download className="w-3.5 h-3.5" />
            Exporter
          </button>
          <button
            onClick={() => navigate('/test-cases/create')}
            className="btn-primary inline-flex items-center gap-1.5 text-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            Nouveau cas de test
          </button>
        </div>
      </div>

      {/* ── Stats strip ─────────────────────────────────────────────────────── */}
      {testCases.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
          {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as TestCasePriority[]).map((p) => (
            <button
              key={p}
              onClick={() => setPriorityFilter((prev) => (prev === p ? '' : p))}
              className={`flex flex-col items-center justify-center p-2 rounded-lg border text-center transition-all ${
                priorityFilter === p ? 'ring-2 ring-amber-400 border-amber-400' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${PRIORITY_BADGE[p]}`}>
                {priorityCounts[p] ?? 0}
              </span>
              <span className="text-xs text-gray-500 mt-1">{PRIORITY_LABEL[p]}</span>
            </button>
          ))}
          {(['DRAFT', 'ACTIVE', 'DEPRECATED'] as TestCaseStatus[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter((prev) => (prev === s ? '' : s))}
              className={`flex flex-col items-center justify-center p-2 rounded-lg border text-center transition-all ${
                statusFilter === s ? 'ring-2 ring-amber-400 border-amber-400' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[s]}`}>
                {statusCounts[s] ?? 0}
              </span>
              <span className="text-xs text-gray-500 mt-1">{STATUS_LABEL[s]}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Filter bar ──────────────────────────────────────────────────────── */}
      <div className="dt-filters bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher (nom, module, tag…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
        </div>
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
        >
          <option value="">Tous les modules</option>
          {TEST_MODULES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TestCaseStatus | '')}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
        >
          <option value="">Tous les statuts</option>
          <option value="DRAFT">Brouillon</option>
          <option value="ACTIVE">Actif</option>
          <option value="DEPRECATED">Déprécié</option>
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as TestCasePriority | '')}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
        >
          <option value="">Toutes priorités</option>
          <option value="CRITICAL">Critique</option>
          <option value="HIGH">Haute</option>
          <option value="MEDIUM">Moyenne</option>
          <option value="LOW">Basse</option>
        </select>
        <select
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
        >
          <option value="">Tous les acteurs</option>
          {TEST_ACTORS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
        {hasActiveFilter && (
          <button
            onClick={() => { setSearch(''); setModuleFilter(''); setStatusFilter(''); setPriorityFilter(''); setActorFilter(''); }}
            className="text-xs text-amber-600 hover:text-amber-800 font-medium"
          >
            Réinitialiser
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Bulk action bar ─────────────────────────────────────────────────── */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <span className="text-sm font-medium text-amber-800">
            {selected.size} sélectionné{selected.size > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            {bulkDeleteConfirm ? (
              <>
                <span className="text-xs text-red-600 font-medium">Confirmer la suppression ?</span>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Oui, supprimer
                </button>
                <button
                  onClick={() => setBulkDeleteConfirm(false)}
                  disabled={bulkDeleting}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setBulkDeleteConfirm(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-md"
                >
                  <Trash2 className="w-3 h-3" />
                  Supprimer ({selected.size})
                </button>
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Désélectionner
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => void load()} className="ml-auto text-xs underline">Réessayer</button>
        </div>
      )}

      {/* ── Loading ─────────────────────────────────────────────────────────── */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Chargement…</span>
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────────── */}
      {!loading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FlaskConical className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">Aucun cas de test</p>
          <p className="text-gray-400 text-sm mt-1">
            {hasActiveFilter ? 'Aucun résultat pour ces filtres.' : 'Créez votre premier cas de test ou importez un fichier JSON.'}
          </p>
          {!hasActiveFilter && (
            <button
              onClick={() => navigate('/test-cases/create')}
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100"
            >
              <Plus className="w-4 h-4" />
              Nouveau cas de test
            </button>
          )}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      {!loading && filtered.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="pl-4 pr-2 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-400 cursor-pointer"
                    />
                  </th>
                  {([
                    ['moduleId', 'Module'],
                    ['name', 'Nom'],
                    ['primaryActor', 'Acteur principal'],
                    ['priority', 'Priorité'],
                    ['status', 'Statut'],
                  ] as [SortField, string][]).map(([field, label]) => (
                    <th
                      key={field}
                      onClick={() => handleSort(field)}
                      className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                    >
                      <span className="flex items-center gap-1">{label} <SortIcon field={field} /></span>
                    </th>
                  ))}
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Étapes
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Exécutions
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider sticky right-0 bg-gray-50 border-l border-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {filtered.map((tc) => (
                  <tr
                    key={tc.id}
                    className={`hover:bg-amber-50 cursor-pointer transition-colors group ${selected.has(tc.id) ? 'bg-amber-50' : ''}`}
                    onClick={() => navigate(`/test-cases/${tc.id}`)}
                  >
                    <td className="pl-4 pr-2 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(tc.id)}
                        onChange={() => toggleSelect(tc.id)}
                        className="rounded border-gray-300 text-amber-500 focus:ring-amber-400 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                        {tc.moduleId}
                      </span>
                    </td>
                    <td className="px-4 py-3 min-w-[220px]">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs">{tc.name}</div>
                      {tc.externalRef && (
                        <div className="text-xs text-gray-400 font-mono mt-0.5">{tc.externalRef}</div>
                      )}
                      {tc.tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap mt-1">
                          {tc.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                          {tc.tags.length > 3 && (
                            <span className="text-[10px] text-gray-400">+{tc.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {actorName(tc.primaryActor)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_BADGE[tc.priority]}`}>
                        {PRIORITY_LABEL[tc.priority]}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[tc.status]}`}>
                        {STATUS_LABEL[tc.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {tc.steps.length} étape{tc.steps.length !== 1 ? 's' : ''}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {tc._count?.instances ?? 0}
                    </td>
                    <td
                      className="px-4 py-3 whitespace-nowrap text-right sticky right-0 bg-white group-hover:bg-amber-50 border-l border-gray-200 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/test-cases/${tc.id}`)}
                          className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-100 rounded"
                          title="Voir le détail"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => navigate(`/test-cases/${tc.id}/execute`)}
                          className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                          title="Nouvelle exécution"
                        >
                          <Play className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => navigate(`/test-cases/${tc.id}/edit`)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Modifier"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ open: true, id: tc.id, name: tc.name })}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.open}
        title="Supprimer le cas de test"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteConfirm.name}" ? Cette action supprime également toutes ses exécutions.`}
        confirmLabel="Supprimer"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
